import dayjs from "dayjs";
import "dayjs/locale/es";
dayjs.locale("es");

/**
 * Pdf/nominaNoTimbradosPdf.js
 *
 * Reporte de los empleados de una prenómina que NO tienen CFDI, con el motivo.
 *
 * Los datos los arma `getDatosNoTimbrados()` en TablaPrenomina.js — el mismo
 * constructor que alimenta la versión en Excel, para que los dos documentos digan
 * exactamente lo mismo. Aquí sólo se maqueta.
 *
 * Paleta de marca BIWO, igual que la Lista de Raya y el resto de los reportes
 * "modernos" de la app.
 */

const C_TURQUESA = "#00BFD6";
const C_NARANJA  = "#FF6A1A";
const C_TEAL_PRO = "#005F6B";
const C_BANDA_GR = "#BFEDF5";
const C_ZEBRA    = "#F5FBFC";
const C_TINTA_SU = "#8C8C8C";
const C_ROJO     = "#E53935";
const C_BORDE    = "#D9D9D9";

const fmtDinero = (n) =>
  (Number(n) || 0).toLocaleString("es-MX", {
    style: "currency", currency: "MXN", minimumFractionDigits: 2,
  });

const nominaNoTimbradosPdf =({ prenomina, filas, totalesNomina, netoNoTimbrado, huboValidacion }) => {
  const fi = prenomina?.fechaInicio ? dayjs(prenomina.fechaInicio).format("DD/MM/YYYY") : "";
  const ff = prenomina?.fechaFin ? dayjs(prenomina.fechaFin).format("DD/MM/YYYY") : "";
  const periodoTxt = [
    prenomina?.periodoNumero != null ? `Periodo ${prenomina.periodoNumero}` : "Periodo",
    prenomina?.tipoPeriodoNombre || "",
    fi && ff ? `· del ${fi} al ${ff}` : "",
  ].filter(Boolean).join(" ");

  // ── Encabezado ──────────────────────────────────────────────────────────
  const encabezado = {
    columns: [
      {
        width: "*",
        stack: [
          { text: "BIWO", style: "biwo" },
          { text: "NÓMINAS", style: "nominas" },
          { text: prenomina?.empresaNombre || "", style: "empresa", margin: [0, 6, 0, 0] },
          { text: "Empleados no timbrados", style: "titulo" },
          { text: periodoTxt, style: "meta" },
        ],
      },
      {
        width: "auto",
        stack: [
          { text: `${filas.length}`, style: "contadorNum", alignment: "right" },
          { text: "sin timbrar", style: "contadorTxt", alignment: "right" },
          {
            text: `de ${totalesNomina.empleados} empleado(s)`,
            style: "meta", alignment: "right",
          },
        ],
      },
    ],
  };

  const regla = {
    canvas: [{ type: "rect", x: 0, y: 0, w: 555, h: 3, color: C_TURQUESA }],
    margin: [0, 8, 0, 10],
  };

  // ── Tabla ───────────────────────────────────────────────────────────────
  const th = (t) => ({ text: t, style: "th" });

  const cuerpo = [
    [th("Código"), th("Empleado"), th("Estatus"), th("Motivo"), th("Neto")],
    ...filas.map((f, i) => {
      const fill = i % 2 === 1 ? C_ZEBRA : null;
      return [
        { text: f.codigo, style: "celda", fillColor: fill },
        {
          stack: [
            { text: f.nombre, style: "celdaFuerte" },
            ...(f.rfc ? [{ text: f.rfc, style: "celdaTenue" }] : []),
            ...(f.departamento ? [{ text: f.departamento, style: "celdaTenue" }] : []),
          ],
          fillColor: fill,
        },
        { text: f.estatus, style: "celda", fillColor: fill },
        {
          stack: [
            { text: f.motivo, style: "motivo" },
            // El detalle es el que nombra el atributo a corregir; sin él, el
            // código del PAC no dice qué hacer.
            ...(f.detalle ? [{ text: f.detalle, style: "celdaTenue", margin: [0, 2, 0, 0] }] : []),
          ],
          fillColor: fill,
        },
        { text: fmtDinero(f.neto), style: "celdaNum", fillColor: fill },
      ];
    }),
  ];

  const tabla = {
    table: {
      headerRows: 1,
      widths: [38, 130, 70, "*", 62],
      body: cuerpo,
    },
    layout: {
      hLineWidth: (i) => (i === 0 || i === 1 ? 0 : 0.5),
      vLineWidth: () => 0,
      hLineColor: () => C_BORDE,
      paddingTop: () => 4,
      paddingBottom: () => 4,
      paddingLeft: () => 5,
      paddingRight: () => 5,
    },
  };

  // ── Totales ─────────────────────────────────────────────────────────────
  // Sólo la suma de los renglones de ESTE reporte. Los totales de toda la nómina
  // se quedan fuera a propósito: el documento se comparte con quien va a corregir
  // los datos y no tiene por qué llevar lo que cobró el resto de la empresa.
  const totales = {
    margin: [0, 12, 0, 0],
    table: {
      widths: [38, 130, 70, "*", 62],
      body: [[
        { text: "NETO PENDIENTE DE TIMBRAR", style: "totalLblFuerte", fillColor: C_BANDA_GR, colSpan: 4 },
        {}, {}, {},
        { text: fmtDinero(netoNoTimbrado), style: "totalNumFuerte", fillColor: C_BANDA_GR },
      ]],
    },
    layout: {
      hLineWidth: (i, node) => (i === 0 || i === node.table.body.length ? 0.8 : 0),
      vLineWidth: () => 0,
      hLineColor: () => C_TEAL_PRO,
      paddingTop: () => 5,
      paddingBottom: () => 5,
      paddingLeft: () => 6,
      paddingRight: () => 6,
    },
  };

  // Sin el diagnóstico de la validación el reporte sigue siendo útil, pero el
  // usuario tiene que saber que los motivos vienen sólo del estatus.
  const avisoSinValidacion = huboValidacion ? [] : [{
    text: "No se pudo consultar la validación previa, así que los motivos de las líneas que "
        + "nunca se intentaron timbrar salen sólo por su estatus.",
    style: "aviso",
    margin: [0, 10, 0, 0],
  }];

  return {
    pageSize: "LETTER",
    pageOrientation: "portrait",
    pageMargins: [28, 28, 28, 34],
    footer: (pagina, total) => ({
      columns: [
        { text: `Generado ${dayjs().format("DD/MM/YYYY HH:mm")}`, style: "pie" },
        { text: `${pagina} / ${total}`, style: "pie", alignment: "right" },
      ],
      margin: [28, 8, 28, 0],
    }),
    content: [encabezado, regla, tabla, totales, ...avisoSinValidacion],
    styles: {
      biwo:        { fontSize: 20, bold: true, color: C_TURQUESA },
      nominas:     { fontSize: 8, bold: true, color: C_NARANJA },
      empresa:     { fontSize: 13, bold: true },
      titulo:      { fontSize: 10, color: C_TINTA_SU, margin: [0, 2, 0, 0] },
      meta:        { fontSize: 8, color: C_TINTA_SU },
      contadorNum: { fontSize: 26, bold: true, color: C_ROJO },
      contadorTxt: { fontSize: 8, bold: true, color: C_ROJO },
      th:          { fontSize: 8, bold: true, color: "#FFFFFF", fillColor: C_TURQUESA, margin: [0, 2, 0, 2] },
      celda:       { fontSize: 7.5 },
      celdaFuerte: { fontSize: 8, bold: true },
      celdaTenue:  { fontSize: 6.5, color: C_TINTA_SU },
      celdaNum:    { fontSize: 8, alignment: "right" },
      motivo:      { fontSize: 7.5, color: C_ROJO },
      totalLblFuerte: { fontSize: 10, bold: true, color: C_TEAL_PRO },
      totalNumFuerte: { fontSize: 10, bold: true, color: C_TEAL_PRO, alignment: "right" },
      aviso:       { fontSize: 7, italics: true, color: C_TINTA_SU },
      pie:         { fontSize: 6.5, color: C_TINTA_SU },
    },
  };
};

export default nominaNoTimbradosPdf;
